import React from 'react';
import { FormattedTextarea } from '../FormattedTextarea';
import { ToggleSwitch } from '../../../sdk';
import { getAuthHeaders } from '../../../utils/authHeaders';

const GUIDE_STYLES = {
  '1:1':  { top: '9%',    left: '6%',    width: '88%',   height: '80%' },
  '4:5':  { top: '9%',    left: '15%',   width: '70%',   height: '80%' },
  '16:9': { top: '24%',   left: '5%',    width: '90%',   height: '52%' },
};

const RESOLUTION_MAP = {
  '1:1':  '1080\u00d71080',
  '4:5':  '1080\u00d71350',
  '16:9': '1920\u00d71080',
};

const BTN_COL_STYLE = { width: 56, flexShrink: 0 };

export function ImageWidgetEditor({ imageWidget, onChange }) {
  const hasWidget = !!imageWidget;
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const previousDataRef = React.useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleToggle = (enabled) => {
    if (enabled) {
      onChange(previousDataRef.current || {
        label: 'Art Hub',
        caption: 'A place where nature meets culture.',
        aspectRatio: '1:1',
        size: 'large',
      });
    } else {
      previousDataRef.current = imageWidget;
      onChange(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const headers = await getAuthHeaders();
      const response = await fetch(`${backendUrl}/api/widgets/upload-image`, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      // Persist stable internal URL — NOT the temporary presigned URL.
      // The stable route /api/media/asset/{id} redirects (302) to a fresh
      // presigned URL at request time, so <img src> rendering works.
      // Hydration will later resolve asset_id → presigned URL for faster
      // subsequent renders. Backend sanitizer is the final safety net.
      onChange({
        ...imageWidget,
        asset_id: data.asset_id,
        src: `/api/media/asset/${data.asset_id}`,
      });
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this image?')) return;
    onChange({ ...imageWidget, asset_id: '', src: '' });
  };

  const openFilePicker = () => {
    if (!uploading) fileInputRef.current?.click();
  };

  const aspectRatios = [
    { value: '1:1', label: '1:1' },
    { value: '4:5', label: '4:5' },
    { value: '16:9', label: '16:9' },
  ];

  const isLandscape = imageWidget?.aspectRatio === '16:9';
  const sizeOptions = isLandscape
    ? [{ value: 'large', label: '100%' }, { value: 'small', label: '75%' }]
    : [{ value: 'large', label: '50%' }, { value: 'small', label: '30%' }];

  const currentRatio = imageWidget?.aspectRatio || '1:1';
  const guideStyle = GUIDE_STYLES[currentRatio] || GUIDE_STYLES['1:1'];
  const resolution = RESOLUTION_MAP[currentRatio] || '';
  const hasFile = !!(imageWidget?.asset_id || imageWidget?.src);

  return (
    <div className="field-container">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="module-header">Image Widget</div>
          <div className="module-desc">Media card with caption.</div>
        </div>
        <ToggleSwitch checked={hasWidget} onChange={handleToggle} size="sm" />
      </div>

      {hasWidget && (
        <div className="mt-3 space-y-3">
          {/* Label */}
          <div>
            <div className="text-[11px] text-white/55 mb-1">Label</div>
            <input
              value={imageWidget.label || ''}
              onChange={(e) => onChange({ ...imageWidget, label: e.target.value })}
              className="input-base"
              placeholder="e.g. Art Hub"
            />
          </div>

          {/* Caption */}
          <div>
            <div className="text-[11px] text-white/55 mb-1">Caption</div>
            <FormattedTextarea
              value={imageWidget.caption || ''}
              onValueChange={(val) => onChange({ ...imageWidget, caption: val })}
              className="textarea-base"
              rows={2}
              maxLength={300}
              placeholder="Description text"
            />
          </div>

          {/* ── [SCREEN] [UPLOAD] [BUTTONS] row ── */}
          <div className="flex items-stretch gap-2">
            {/* LEFT: Cinema monitor (display only) */}
            <div className="media-cinema">
              <div className="media-cinema-inner">
                <span className="media-cinema-label" style={{ top: 6, left: 8 }}>{currentRatio}</span>
                <span className="media-cinema-label" style={{ top: 6, right: 8 }}>{resolution}</span>
                <div className="media-cinema-guide" style={guideStyle} />
                {!hasFile && !uploading && (
                  <div className="media-cinema-crosshair" style={{
                    top: `calc(${guideStyle.top} + ${guideStyle.height} / 2)`,
                    left: `calc(${guideStyle.left} + ${guideStyle.width} / 2)`,
                  }}>+</div>
                )}
                {hasFile && (
                  <>
                    <div className="media-cinema-media" style={guideStyle}>
                      <img src={imageWidget.src} alt="Preview" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className="media-cinema-delete" onClick={handleRemove} title="Remove image">
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12" /></svg>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* CENTER: Standard upload dropzone */}
            <div
              className={`upload-dropzone upload-dropzone-inline${uploading ? ' upload-dropzone-loading' : ''}`}
              onClick={openFilePicker}
            >
              <div className="upload-dropzone-content">
                {uploading ? (
                  <div className="upload-dropzone-text">Uploading...</div>
                ) : (
                  <>
                    <svg className="upload-dropzone-icon" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div className="upload-dropzone-text">{hasFile ? 'Replace' : 'Upload'}</div>
                    <div className="upload-dropzone-sub">PNG / JPG / WebP</div>
                  </>
                )}
              </div>
            </div>

            {/* RIGHT: Buttons — fixed width */}
            <div className="flex flex-col gap-1 justify-center" style={BTN_COL_STYLE}>
              <span className="opt-group-label text-center">Ratio</span>
              {aspectRatios.map((r) => (
                <button
                  key={r.value}
                  onClick={() => onChange({ ...imageWidget, aspectRatio: r.value, size: 'large', ...(r.value !== '16:9' ? { expandVertical: false } : {}) })}
                  className={`opt-cell${currentRatio === r.value ? ' opt-cell-active' : ''}`}
                  style={{ justifyContent: 'center', padding: '0 6px' }}
                >
                  <div className={`opt-cell-led${currentRatio === r.value ? ' opt-cell-led-active' : ''}`} />
                  {r.label}
                </button>
              ))}
              <div style={{ height: 3 }} />
              <span className="opt-group-label text-center">Size</span>
              {sizeOptions.map((s) => (
                <button
                  key={s.value}
                  onClick={() => onChange({ ...imageWidget, size: s.value })}
                  className={`opt-cell${(imageWidget.size || 'large') === s.value ? ' opt-cell-active' : ''}`}
                  style={{ justifyContent: 'center', padding: '0 6px' }}
                >
                  <div className={`opt-cell-led${(imageWidget.size || 'large') === s.value ? ' opt-cell-led-active' : ''}`} />
                  {s.label}
                </button>
              ))}
              {/* Expand toggle — fixed height container */}
              <div style={{ height: 22, marginTop: 2 }}>
                {isLandscape && (
                  <label className="flex items-center gap-1 cursor-pointer select-none" title="Expand vertically">
                    <ToggleSwitch
                      checked={!!imageWidget.expandVertical}
                      onChange={(v) => onChange({ ...imageWidget, expandVertical: v })}
                      size="sm"
                    />
                    <span style={{ fontSize: 7, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.3px' }}>Exp</span>
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Status LEDs below the row */}
          <div className="media-status-row">
            <div className="media-status-item" title={hasFile ? 'File loaded' : 'No file'}>
              <svg className="media-status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <div className={`media-status-led${hasFile ? ' media-status-led-on-green' : ''}`} />
            </div>
            <div className="media-status-item" title={imageWidget.asset_id ? 'Cloud stored' : 'Not in cloud'}>
              <svg className="media-status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
              </svg>
              <div className={`media-status-led${imageWidget.asset_id ? ' media-status-led-on-blue' : ''}`} />
            </div>
          </div>

          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
        </div>
      )}
    </div>
  );
}
